import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useAddons } from "@/contexts/AddonsContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { initials, fmtTime, fmtDate, fmtDateOpts, timezoneLabel } from "@/lib/helpers";
import { MagnifyingGlass, SignIn, SignOut, Baby, Stethoscope, WifiSlash, CheckCircle, CalendarBlank, X, UsersFour, Receipt, Warning, ArrowUpRight } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { isOnline, enqueueCheckIn, queueSize } from "@/lib/offlineQueue";
import StaffAttendanceList, { StatusBadge } from "@/components/attendance/StaffAttendanceList";
import LeaveRequestsPanel from "@/components/attendance/LeaveRequestsPanel";
import AttendanceCorrectionsPanel from "@/components/attendance/AttendanceCorrectionsPanel";
import ExportBar from "@/components/attendance/ExportBar";
import SelfCheckInCard from "@/components/attendance/SelfCheckInCard";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Format minutes as "45m" for < 60, or "1h 15m" for >= 60.
function fmtDuration(min) {
  const m = Math.max(0, Math.round(min || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export default function AttendancePage() {
  const { user, activeBranch, activeRole } = useAuth();
  const { can } = usePermissions();
  // iter-67j (Area 1a) — Only users with sessions:full-equivalent
  // Attendance (i.e. attendance:full) can download rosters. View-only
  // and limited callers have the ExportBar removed from the DOM.
  const canExportAttendance = can("attendance", "full");
  const { hasEnterpriseWorkforce } = useAddons();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [staffRoster, setStaffRoster] = useState([]);
  const [staffAttendance, setStaffAttendance] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [attendance, setAttendance] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [queue, setQueue] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("students");
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(isOnline());
  const [pending, setPending] = useState(queueSize());
  // Filters
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  // iter-55 — Status filter across Student / Therapist / Other Staff tabs.
  // "any" = no filter. Filters both today's attByPerson view and the
  // history view.
  const [filterStatus, setFilterStatus] = useState("any");
  // iter-67 — History mode is now DATE-only. The removed student /
  // therapist dropdowns no longer force the page into history view —
  // typing a name / ID / email now filters the live check-in roster
  // instead of swapping to the history table.
  const isHistoryMode = dateFrom !== todayISO() || dateTo !== todayISO();
  const [historyRows, setHistoryRows] = useState([]);

  // iter-67 — Contact visibility gate. Owner / admin / admin_* /
  // reception / receptionist can see email addresses on attendance
  // rows. Everyone else (therapists, clerks, guardians) sees names
  // only. Matching against email still happens silently for every
  // user so an admin sharing a search term with reception gets the
  // same hits.
  const canViewContacts = useMemo(() => {
    const roles = new Set((user?.roles || []).map(r => String(r).toLowerCase()));
    if (roles.has("owner") || roles.has("admin") || roles.has("reception") || roles.has("receptionist")) return true;
    for (const r of roles) if (String(r).startsWith("admin_")) return true;
    return false;
  }, [user]);

  // iter-67 — Clickable-name permission gate. Owner + Admin (matches
  // the /workforce/employees route allow-list and the existing student
  // "Full Access" scope). Non-privileged roles see the name as plain
  // text.
  const canOpenEmployee = useMemo(() => {
    const roles = new Set((user?.roles || []).map(r => String(r).toLowerCase()));
    return roles.has("owner") || roles.has("admin");
  }, [user]);

  useEffect(() => {
    const on = () => { setOnline(true); setPending(queueSize()); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const t = setInterval(() => setPending(queueSize()), 3000);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(t); };
  }, []);

  const branchId = activeBranch === "all" ? null : activeBranch;

  const load = async () => {
    setLoading(true);
    try {
      const [stRes, thRes, attRes, ttRes, qRes, staffRes] = await Promise.all([
        api.get("/students", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/users/therapists", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/attendance/today", { params: branchId ? { branch_id: branchId } : {} }),
        api.get("/therapy-types"),
        api.get("/queue/students", { params: { include_done: true, ...(branchId ? { branch_id: branchId } : {}) } }),
        // Staff roster + today's attendance — 403s for non-admins; we
        // just fall back to an empty roster in that case.
        api.get("/attendance/staff", { params: branchId ? { branch_id: branchId } : {} }).catch(() => ({ data: null })),
      ]);
      setStudents(stRes.data || []);
      setTherapists(thRes.data || []);
      setAttendance(attRes.data || []);
      setTherapyTypes(ttRes.data || []);
      setQueue(qRes.data || []);
      if (staffRes.data) {
        setStaffRoster(staffRes.data.roster || []);
        setStaffAttendance(staffRes.data.attendance || []);
        setIsAdmin(true);
      } else {
        setStaffRoster([]);
        setStaffAttendance([]);
        setIsAdmin(false);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeBranch]); // eslint-disable-line

  // Load historical attendance when filters go beyond "today only"
  useEffect(() => {
    if (!isHistoryMode) { setHistoryRows([]); return; }
    let cancelled = false;
    (async () => {
      try {
        // iter-67 — history query is now DATE + branch only. The
        // removed student / therapist dropdown filters no longer
        // scope the fetch — the text search filters client-side.
        const params = {
          date_from: dateFrom,
          date_to: dateTo,
          ...(branchId ? { branch_id: branchId } : {}),
        };
        const { data } = await api.get("/attendance", { params });
        if (!cancelled) setHistoryRows(data || []);
      } catch (e) { if (!cancelled) toast.error(formatApiError(e)); }
    })();
    return () => { cancelled = true; };
  }, [isHistoryMode, dateFrom, dateTo, branchId]);

  const clearFilters = () => {
    setDateFrom(todayISO());
    setDateTo(todayISO());
    setFilterStatus("any");
    setSearch("");
  };
  const resetToToday = () => {
    setDateFrom(todayISO());
    setDateTo(todayISO());
  };

  // One row per person for the live roster. With students now able to
  // check in several times a day, prefer the OPEN visit; otherwise show
  // the most recent closed one.
  const attByPerson = useMemo(() => {
    const m = {};
    for (const a of attendance) {
      const cur = m[a.subject_id];
      if (!cur) { m[a.subject_id] = a; continue; }
      const curOpen = !cur.time_out;
      const aOpen = !a.time_out;
      if (aOpen && !curOpen) { m[a.subject_id] = a; continue; }
      if (aOpen === curOpen && (a.time_in || "") > (cur.time_in || "")) m[a.subject_id] = a;
    }
    return m;
  }, [attendance]);

  // How many visits each person has already recorded today.
  const attCountByPerson = useMemo(() => {
    const m = {};
    for (const a of attendance) m[a.subject_id] = (m[a.subject_id] || 0) + 1;
    return m;
  }, [attendance]);

  const queueByStudent = useMemo(() => {
    const m = {};
    for (const q of queue) m[q.student_id] = q;
    return m;
  }, [queue]);

  const therapyById = useMemo(() => {
    const m = {};
    for (const t of therapyTypes) m[t.id] = t;
    return m;
  }, [therapyTypes]);

  const doCheckIn = async (subject, subject_type) => {
    if (activeBranch === "all") { toast.error("Pick a branch first"); return; }
    // Iter-45 — preflight guard: students must have at least one therapy
    // on their profile before check-in. Backend enforces the same check,
    // this client-side pre-check gives a nicer inline block message with
    // a direct link to fix.
    if (subject_type === "student" && !(subject.therapies || []).length) {
      const canEdit = ["owner", "admin", "admin_student"].includes(activeRole);
      toast.error(
        `${subject.name} has no therapies assigned. ` +
          `${canEdit ? "Open the profile to add one." : "Ask an admin to add a therapy."}`,
        {
          duration: 6000,
          action: {
            label: canEdit ? "Edit profile" : "View profile",
            onClick: () => navigate(`/students/${subject.id}${canEdit ? "?edit=1" : ""}`),
          },
          id: `no-therapy-${subject.id}`,
        },
      );
      return;
    }
    const payload = {
      subject_id: subject.id,
      subject_type,
      branch_id: subject.branch_id || activeBranch,
    };
    if (!isOnline()) {
      enqueueCheckIn(payload);
      toast.success(`Queued ${subject.name} — will sync when back online (${queueSize()} pending)`);
      return;
    }
    try {
      await api.post("/attendance/check-in", payload);
      toast.success(`${subject.name} checked in`);
      load();
    } catch (e) {
      // If network dropped mid-request, fall back to queue
      if (!isOnline() || !e.response) {
        enqueueCheckIn(payload);
        toast.success(`Queued offline (${queueSize()} pending)`);
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  const doCheckOut = async (attId, name) => {
    try {
      await api.post(`/attendance/check-out/${attId}`);
      toast.success(`${name} checked out`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // iter-67 — Consolidated Attendance search. One text field for the
  // whole page, matches across every attendance-participant type
  // (students / therapists / other staff) on:
  //   • name
  //   • student_id (workspace-unique)
  //   • student_number (legacy — kept for backwards compat)
  //   • employee_number
  //   • email (matches SILENTLY — email is redacted for roles without
  //     contact-view permission; see canViewContacts + PersonList
  //     below)
  const filter = (list) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((x) => {
      const hay = [
        x.name, x.student_id, x.student_number, x.employee_number, x.email,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  };
  // iter-55 — Status-filter helper. Filters by primary status OR modifier
  // for the "late" / "early_exit" filters (they can appear either way).
  const filterByStatus = (list) => {
    if (filterStatus === "any") return list;
    return list.filter((p) => {
      const att = attByPerson[p.id];
      if (!att) return filterStatus === "no_record";
      const status = String(att.status || "").toLowerCase();
      const modifiers = (att.modifiers || []).map((m) => String(m).toLowerCase());
      if (filterStatus === "late") return status === "late" || modifiers.includes("late_arrival");
      if (filterStatus === "early_exit") return status === "early_exit" || modifiers.includes("early_exit");
      if (filterStatus === "leave") return status.endsWith("_leave") || status === "leave";
      return status === filterStatus;
    });
  };
  const filterFinal = (list) => filterByStatus(filter(list));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline mb-1.5">Today · {fmtDateOpts(new Date().toISOString(), { weekday:"long", day:"numeric", month:"long"})} · {timezoneLabel()}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Attendance</h1>
          <p className="text-sm text-muted-foreground mt-1">Check students in and out, mark staff attendance, review leave requests.</p>
        </div>
        <div className="flex items-center gap-2">
          {!online && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-100 border border-amber-200 text-amber-800 text-xs font-medium" data-testid="offline-badge">
              <WifiSlash size={14} weight="duotone" /> Offline · {pending} queued
            </div>
          )}
          {online && pending > 0 && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium">
              Syncing {pending}…
            </div>
          )}
          <div className="relative w-full md:w-80">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search by Name, ID or Email" className="pl-9" data-testid="attendance-search" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="students" data-testid="tab-students-attendance"><Baby size={14} className="mr-1.5" weight="duotone" />Students</TabsTrigger>
          <TabsTrigger value="therapists" data-testid="tab-staff-attendance"><UsersFour size={14} className="mr-1.5" weight="duotone" /><span className="hidden sm:inline">Therapist and Other Staff</span><span className="sm:hidden">Staff</span></TabsTrigger>
          {hasEnterpriseWorkforce && isAdmin && (
            <TabsTrigger value="corrections" data-testid="tab-corrections">
              <Receipt size={14} className="mr-1.5" weight="duotone" />
              <span className="hidden sm:inline">Corrections</span>
              <span className="sm:hidden">Fixes</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* iter-66 — Self check-in card. Renders for every logged-in
            staff member (owner / admin / therapist / receptionist /
            clerk / etc.) so they can manually mark themselves present
            without going through a QR scan or asking an admin. Hidden
            for parents / guardians and for the "All branches" view.
            Sits above the filter bar so it's the first thing the user
            sees when opening the page. */}
        <div className="mt-4">
          <SelfCheckInCard
            user={user}
            activeBranch={activeBranch}
            attendance={attendance}
            onChanged={load}
          />
        </div>

        {/* Filter bar */}
        <Card className="mt-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">From</Label>
                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="att-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">To</Label>
                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="att-to" />
              </div>
              <div className="space-y-1 col-span-2 md:col-span-1">
                <Label className="text-[11px] text-muted-foreground">Status</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger data-testid="att-status-filter"><SelectValue placeholder="Any status" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="any" data-testid="status-any">Any status</SelectItem>
                    <SelectItem value="present" data-testid="status-present">Present</SelectItem>
                    <SelectItem value="absent" data-testid="status-absent">Absent</SelectItem>
                    <SelectItem value="half_day" data-testid="status-half-day">Half-Day</SelectItem>
                    <SelectItem value="late" data-testid="status-late">Late arrival</SelectItem>
                    <SelectItem value="early_exit" data-testid="status-early-exit">Early exit</SelectItem>
                    <SelectItem value="leave" data-testid="status-leave">Leave</SelectItem>
                    <SelectItem value="holiday" data-testid="status-holiday">Holiday</SelectItem>
                    <SelectItem value="incomplete" data-testid="status-incomplete">Incomplete</SelectItem>
                    <SelectItem value="no_record" data-testid="status-no-record">No record</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 col-span-2 md:col-span-2">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={resetToToday} data-testid="att-today-btn">
                  <CalendarBlank size={14} weight="duotone" /> Today
                </Button>
                {isHistoryMode && (
                  <Button size="sm" variant="ghost" className="gap-1.5" onClick={clearFilters} data-testid="att-clear-btn">
                    <X size={14} weight="bold" /> Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* iter-66 — Export bars ALWAYS visible, in both today-view and
            history-view. The bar reflects the currently active tab so
            operators can grab the current filter as CSV / Excel
            regardless of the date range picked.
            iter-67j (Area 1a + 1b) — Only rendered when the caller has
            attendance:full. Date params are ALWAYS populated (fall back
            to today) so the exported file mirrors the visible list. */}
        {canExportAttendance && (
        <div className="flex justify-end mt-4">
          {tab === "students" ? (
            <ExportBar
              endpoint="/exports/student-attendance"
              params={{
                date_from: dateFrom || todayISO(),
                date_to: dateTo || todayISO(),
                branch_id: branchId || undefined,
                status: filterStatus !== "any" ? filterStatus : undefined,
                name_search: search || undefined,
              }}
              matchCount={isHistoryMode
                ? historyRows.filter(r => r.subject_type === "student").length
                : filterFinal(students).length}
              emptyMessage="No matching student attendance records to export"
              testIdPrefix="export-student-attendance"
            />
          ) : tab === "therapists" ? (
            <ExportBar
              endpoint="/exports/staff-attendance"
              params={{
                date_from: dateFrom || todayISO(),
                date_to: dateTo || todayISO(),
                branch_id: branchId || undefined,
                status: filterStatus !== "any" ? filterStatus : undefined,
                name_search: search || undefined,
              }}
              matchCount={isHistoryMode
                ? historyRows.filter(r => r.subject_type !== "student").length
                : staffRoster.length}
              emptyMessage="No matching staff attendance records to export"
              testIdPrefix="export-staff-attendance"
            />
          ) : null}
        </div>
        )}

        {isHistoryMode ? (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-base font-display tracking-tight">
                Attendance history ({historyRows.length})
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {fmtDate(dateFrom)}{dateFrom !== dateTo && ` – ${fmtDate(dateTo)}`}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <HistoryList
                rows={historyRows}
                students={students}
                therapists={therapists}
              />
            </CardContent>
          </Card>
        ) : (<>

        <TabsContent value="students" className="mt-4 space-y-3">
          <Card>
            <CardHeader><CardTitle className="text-base font-display tracking-tight">Students ({filterFinal(students).length})</CardTitle></CardHeader>
            <CardContent className="p-0">
              {loading ? <div className="p-6 text-muted-foreground text-sm">Loading…</div> :
                <PersonList
                  list={filterFinal(students)}
                  attByPerson={attByPerson}
                  attCountByPerson={attCountByPerson}
                  queueByStudent={queueByStudent}
                  therapyById={therapyById}
                  onOpenSession={(sid) => navigate(`/sessions/${sid}`)}
                  onCheckIn={(s)=>doCheckIn(s, "student")}
                  onCheckOut={(a, name)=>doCheckOut(a, name)}
                  emptyText="No students in this branch."
                  personType="student"
                  activeRole={activeRole}
                  navigate={navigate}
                  canViewContacts={canViewContacts}
                  canOpenEmployee={canOpenEmployee}
                />
              }
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="therapists" className="mt-4 space-y-4">
          {isAdmin ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-display tracking-tight flex items-center gap-2">
                    <UsersFour size={16} weight="duotone" className="text-primary" />
                    Therapist and Other Staff ({staffRoster.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {loading ? <div className="p-6 text-muted-foreground text-sm">Loading…</div> :
                    <StaffAttendanceList
                      roster={staffRoster}
                      attendanceByPerson={Object.fromEntries(staffAttendance.map(a => [a.subject_id, a]))}
                      branchId={activeBranch}
                      onChanged={load}
                      search={search}
                      statusFilter={filterStatus}
                      canViewContacts={canViewContacts}
                      canOpenEmployee={canOpenEmployee}
                    />
                  }
                </CardContent>
              </Card>
              <LeaveRequestsPanel roster={staffRoster} isAdmin={isAdmin} />
            </>
          ) : (
            // Non-admin fallback: legacy therapist-only view (self only) +
            // Leave panel where the user can submit their own requests.
            <>
              <Card>
                <CardHeader><CardTitle className="text-base font-display tracking-tight">Therapists ({filterFinal(therapists).length})</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {loading ? <div className="p-6 text-muted-foreground text-sm">Loading…</div> :
                    <PersonList
                      list={filterFinal(therapists)}
                      attByPerson={attByPerson}
                      onCheckIn={(t)=>doCheckIn({...t, branch_id: activeBranch}, "therapist")}
                      onCheckOut={(a, name)=>doCheckOut(a, name)}
                      emptyText="No therapists in this branch."
                      personType="therapist"
                      canViewContacts={canViewContacts}
                      canOpenEmployee={canOpenEmployee}
                      navigate={navigate}
                    />
                  }
                </CardContent>
              </Card>
              <LeaveRequestsPanel roster={[]} isAdmin={false} />
            </>
          )}
        </TabsContent>

        {hasEnterpriseWorkforce && isAdmin && (
          <TabsContent value="corrections" className="mt-4">
            <AttendanceCorrectionsPanel />
          </TabsContent>
        )}
        </>)}
      </Tabs>
    </div>
  );
}

function HistoryList({ rows, students, therapists }) {
  if (!rows.length) {
    return <div className="p-8 text-center text-sm text-muted-foreground">No attendance records match these filters.</div>;
  }
  const nameOf = (r) => {
    if (r.subject_type === "student") return students.find(s => s.id === r.subject_id)?.name || "Unknown student";
    return therapists.find(t => t.id === r.subject_id)?.name || "Unknown therapist";
  };
  return (
    <div className="divide-y divide-border">
      {rows.map(r => (
        <div key={r.id} className="px-5 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`history-row-${r.id}`}>
          <Avatar className="h-9 w-9">
            <AvatarFallback className={`text-xs font-semibold ${r.subject_type === "student" ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent"}`}>
              {initials(nameOf(r))}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-[180px]">
            <div className="font-medium truncate">{nameOf(r)}</div>
            <div className="text-xs text-muted-foreground">
              {fmtDate(r.date)} · In {fmtTime(r.time_in)} {r.time_out && `· Out ${fmtTime(r.time_out)}`}
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] uppercase">{r.subject_type}</Badge>
          <Badge className={r.status === "present" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-muted text-muted-foreground"}>{r.status}</Badge>
        </div>
      ))}
    </div>
  );
}

function PersonList({ list, attByPerson, attCountByPerson, queueByStudent, therapyById, onOpenSession, onCheckIn, onCheckOut, emptyText, personType, activeRole, navigate, canViewContacts, canOpenEmployee = false }) {
  if (list.length === 0) return <div className="p-6 text-sm text-muted-foreground">{emptyText}</div>;
  const canEditStudent = ["owner", "admin", "admin_student"].includes(activeRole);
  const isStudentSection = personType === "student";
  return (
    <div className="divide-y divide-border">
      {list.map((p) => {
        const att = attByPerson[p.id];
        const isIn = att && !att.time_out;
        const isOut = att && att.time_out;
        const qEntry = personType === "student" ? queueByStudent?.[p.id] : null;
        const pendingIds = (qEntry?.therapies_pending || []);
        const completed = (qEntry?.completed_therapies || []);
        const activeTherapyId = qEntry?.status === "in_session" ? qEntry?.active_therapy_id : null;
        const activeSessionId = qEntry?.status === "in_session" ? qEntry?.session_id : null;
        const isStudent = isStudentSection;
        // Iter-45 — a student profile without therapies cannot enter the
        // queue because it can't be assigned to a therapist. Flag it here
        // and block the check-in button (backend also rejects).
        const noTherapies = personType === "student" && !(p.therapies || []).length;
        // A student who has checked out can check in again — same button,
        // same flow. Staff keep one visit per day.
        const canRecheckIn = isStudentSection && Boolean(isOut);
        const visitsToday = attCountByPerson?.[p.id] || 0;
        return (
          <div key={p.id} className={`flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-muted/40 ${noTherapies && !att ? "bg-amber-50/40" : ""}`} data-testid={`person-row-${p.id}`}>
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">{initials(p.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-[180px]">
              <div className="font-medium text-sm truncate flex items-center gap-2 flex-wrap">
                {/* iter-67 — Clickable name (owner / admin / admin_student).
                    Non-privileged roles see plain text. */}
                {isStudent && canEditStudent ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/students/${p.id}`)}
                    className="truncate text-left hover:text-primary hover:underline underline-offset-2 transition-colors"
                    data-testid={`student-name-link-${p.id}`}
                  >
                    {p.name}
                  </button>
                ) : (!isStudent && canOpenEmployee && p.employee_id) ? (
                  <button
                    type="button"
                    onClick={() => navigate(`/workforce/employees?highlight=${p.employee_id}`)}
                    className="truncate text-left hover:text-primary hover:underline underline-offset-2 transition-colors"
                    data-testid={`employee-name-link-${p.id}`}
                  >
                    {p.name}
                  </button>
                ) : (
                  <span className="truncate">{p.name}</span>
                )}
                {noTherapies && (
                  <Badge
                    className="bg-amber-100 text-amber-900 border-amber-300 gap-1 text-[10px]"
                    data-testid={`no-therapies-badge-${p.id}`}
                  >
                    <Warning size={10} weight="fill" />
                    No therapies assigned
                  </Badge>
                )}
              </div>
              {/* iter-67 — ID sub-line in smaller, muted mono. Hidden
                  entirely when the field is missing so no null / undefined
                  or blank pill leaks. */}
              {isStudent
                ? (p.student_id && (
                  <div
                    className="font-mono text-[10px] font-semibold tabular-nums text-muted-foreground truncate"
                    data-testid={`student-id-sub-${p.id}`}
                  >
                    {p.student_id}
                  </div>
                ))
                : (p.employee_number && (
                  <div
                    className="font-mono text-[10px] font-semibold tabular-nums text-muted-foreground truncate"
                    data-testid={`employee-id-sub-${p.id}`}
                  >
                    {p.employee_number}
                  </div>
                ))
              }
              <div className="text-xs text-muted-foreground truncate">
                {(canViewContacts ? p.email : null) || p.diagnosis || "—"}
              </div>
              {noTherapies && (
                <button
                  type="button"
                  onClick={() => navigate(`/students/${p.id}${canEditStudent ? "?edit=1" : ""}`)}
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-800 hover:text-amber-900 underline underline-offset-2"
                  data-testid={`no-therapies-link-${p.id}`}
                >
                  {canEditStudent ? "Add a therapy on the profile" : "View student profile"}
                  <ArrowUpRight size={11} weight="bold" />
                </button>
              )}
              {/* Therapy status badges (students only) */}
              {personType === "student" && (pendingIds.length > 0 || completed.length > 0) && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`therapy-badges-${p.id}`}>
                  {pendingIds.map(tid => {
                    const t = therapyById?.[tid];
                    if (!t) return null;
                    const isActive = activeTherapyId === tid;
                    if (isActive && activeSessionId) {
                      return (
                        <button
                          key={tid}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onOpenSession?.(activeSessionId); }}
                          className="inline-flex items-center gap-1 rounded-md border border-teal-300 bg-teal-50 text-teal-800 px-1.5 py-0.5 text-[10px] font-medium hover:bg-teal-100 transition-colors"
                          data-testid={`in-progress-badge-${tid}`}
                          title={`${t.name} — in session, click to open`}
                        >
                          <span className="relative inline-flex h-1.5 w-1.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-500 pulse-dot" /></span>
                          {t.code} · in session
                        </button>
                      );
                    }
                    return (
                      <Badge
                        key={tid}
                        variant="outline"
                        className="text-[10px]"
                        style={{ borderColor: `${t.color}66`, color: t.color }}
                        data-testid={`pending-badge-${p.id}-${tid}`}
                      >
                        {t.code}
                      </Badge>
                    );
                  })}
                  {completed.map(c => (
                    <button
                      key={c.session_id}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onOpenSession?.(c.session_id); }}
                      title={`View completed ${c.code} session`}
                      className="inline-flex items-center gap-1 rounded-md border bg-emerald-50 border-emerald-200 text-emerald-800 px-1.5 py-0.5 text-[10px] font-medium hover:bg-emerald-100 transition-colors"
                      data-testid={`completed-badge-${c.session_id}`}
                    >
                      <CheckCircle size={10} weight="fill" />
                      {c.code} {fmtDuration(c.duration_min)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {att && (
              <div className="text-xs text-right hidden sm:block">
                <div>In: <span className="text-foreground font-medium">{fmtTime(att.time_in)}</span></div>
                {att.time_out && <div>Out: <span className="text-foreground font-medium">{fmtTime(att.time_out)}</span></div>}
                {visitsToday > 1 && (
                  <div className="text-[10px] text-muted-foreground" data-testid={`visits-today-${p.id}`}>
                    {visitsToday} visits today
                  </div>
                )}
              </div>
            )}
            {(!att || canRecheckIn) && !noTherapies && (
              <Button size="sm" onClick={() => onCheckIn(p)} className="gap-1.5" data-testid={`checkin-${p.id}`}>
                <SignIn size={14} /> Check In
              </Button>
            )}
            {!att && noTherapies && (
              <div
                className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded-md border border-amber-300 bg-amber-100 text-amber-900"
                data-testid={`checkin-blocked-${p.id}`}
                title="Check-in blocked — this student has no therapies on their profile"
              >
                Check-in blocked
              </div>
            )}
            {isIn && (
              <>
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Present</Badge>
                <Button size="sm" variant="outline" onClick={() => onCheckOut(att.id, p.name)} className="gap-1.5" data-testid={`checkout-${p.id}`}>
                  <SignOut size={14} /> Check Out
                </Button>
              </>
            )}
            {isOut && <Badge variant="outline" className="text-muted-foreground">Left</Badge>}
          </div>
        );
      })}
    </div>
  );
}
